import { memo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
} from '@mui/material';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import useFormSubmit from 'hooks/useFormSubmit';
import useNotification from 'hooks/useNotification';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

const ObjectAddKeyModal = ({ open, setOpen, selected, setSelected }) => {
  const { t } = useTranslation();
  const sendNotification = useNotification();
  const { submit, isSubmitting } = useFormSubmit();
  const axiosPrivate = useAxiosPrivate();
  const { id: object_id } = selected;
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formik = useFormik({
    initialValues: {
      object_id,
      key: '',
      show_price: true,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        values,
        `/admin/object/keygenerate/${object_id}`,
        values.name,
        null,
        true,
        (res) => {
          formik.setFieldValue('key', res?.data);
        },
      );
    },
  });

  // Modal ochilganda mavjud kalitni yuklaymiz (Read)
  useEffect(() => {
    if (!open || !object_id) return;
    let active = true;
    setLoading(true);
    axiosPrivate
      .get(`/admin/object/key/${object_id}`)
      .then((res) => {
        if (!active) return;
        formik.setFieldValue('key', res?.data?.data?.key || '');
        formik.setFieldValue('show_price', res?.data?.data?.show_price ?? true);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, object_id]);

  // Kalitni o'chirish (Delete)
  const handleDelete = async () => {
    if (!object_id) return;
    try {
      setDeleting(true);
      await axiosPrivate.delete(`/admin/object/key/${object_id}`);
      formik.setFieldValue('key', '');
      sendNotification({
        msg: t('common.alerts.success.deleted', {
          value: t('common.table.keyword'),
        }),
        variant: 'success',
      });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || error?.message,
        variant: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  // Narx ko'rsatish holatini almashtirish
  const handleTogglePrice = async (checked) => {
    formik.setFieldValue('show_price', checked);
    if (!object_id || !formik.values.key) return; // kalit yo'q bo'lsa — generate'da qo'llanadi
    try {
      await axiosPrivate.post(`/admin/object/key/${object_id}/price`, {
        show_price: checked,
      });
      sendNotification({
        msg: t('common.alerts.success.updated', {
          value: t('common.table.keyword'),
        }),
        variant: 'success',
      });
    } catch (error) {
      formik.setFieldValue('show_price', !checked); // xato — qaytaramiz
      sendNotification({
        msg: error?.response?.data?.message || error?.message,
        variant: 'error',
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelected({});
    formik.resetForm();
  };

  const hasKey = Boolean(formik.values.key);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      disableEscapeKeyDown={true}
      className='!base-border !shadow-none'
      PaperProps={{
        className: '!shadow-none !base-border min-w-[500px]',
      }}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!bg-[#CDCDCDC7] !text-center !mb-2 !border-b !border-b-[#E6E6E6]'
      >
        <span className='pr-5 font-semibold text-[21px] text-[#374957]'>
          {t('common.table.keyword')}
        </span>
      </DialogTitle>

      <DialogContent>
        {isSubmitting || loading || deleting ? (
          <div className='circular-progress-box py-5 absolute inset-0 z-10 bg-[rgba(255,255,255,.6)]'>
            <CircularProgress size={35} />
          </div>
        ) : (
          ''
        )}
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, lg: 3 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, lg: 12 }}
          >
            <Grid item={true} sm={12} xs={12}>
              <div className='flex justify-between gap-1'>
                <div className='grow border h-[40px] text-ellipsis overflow-hidden whitespace-nowrap rounded-md px-2 flex items-center'>
                  {formik.values.key || (
                    <span className='text-gray-400'>
                      {t('common.global.noDataFound')}
                    </span>
                  )}
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(formik?.values?.key);
                    sendNotification({
                      msg: t('common.alerts.success.copied'),
                      variant: 'success',
                    });
                  }}
                  disabled={!hasKey}
                  variant='outlined'
                  color='info'
                  className='!rounded-md'
                >
                  <i className='bi bi-copy' />
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={!hasKey || deleting}
                  variant='outlined'
                  color='error'
                  className='!rounded-md'
                  title={t('common.button.delete')}
                >
                  <i className='bi bi-trash' />
                </Button>
              </div>
            </Grid>
            <Grid item={true} sm={12} xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formik.values.show_price)}
                    onChange={(e) => handleTogglePrice(e.target.checked)}
                    color='primary'
                  />
                }
                label={t('object.showPrice')}
              />
            </Grid>
            <Grid item={true} sm={12} xs={12}>
              <div className='flex justify-end'>
                <FormActionButtons
                  delay={0}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  saveTitle='common.button.generateKeygen'
                  setOpen={setOpen}
                  reset={formik.resetForm}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ObjectAddKeyModal);
